/**
 * Ability Grounds lab contract shared by the map, client panel, and server.
 *
 * The lab bypass is deliberately narrow: the server additionally requires
 * MEMORY_ONLY_MODE and a GM-authorized session before honoring free casts.
 * Keeping authored coordinates and wire names here prevents the UI, map, and
 * server reset path from drifting apart.
 */
export const ABILITY_GROUNDS_MAP_ID = 'ability-grounds-arena' as const;

export const ABILITY_GROUNDS_PREPARE_MESSAGE = 'debugPrepareAbilityGrounds' as const;
export const ABILITY_GROUNDS_PREPARED_MESSAGE = 'debugAbilityGroundsPrepared' as const;

export const ABILITY_GROUNDS_FLOOR_Y = 600;
export const ABILITY_GROUNDS_SPAWN = Object.freeze({ x: 160, y: 560 });

export const ABILITY_GROUNDS_TARGETS = Object.freeze([
  Object.freeze({ id: 'grounds_dummy_1', label: 'Near', distance: 300, x: 460 }),
  Object.freeze({ id: 'grounds_dummy_2', label: 'Mid', distance: 550, x: 710 }),
  Object.freeze({ id: 'grounds_dummy_3', label: 'Far', distance: 800, x: 960 }),
  Object.freeze({ id: 'grounds_dummy_4', label: 'Max', distance: 1050, x: 1210 }),
]);

/**
 * Non-selected authored dummies are parked behind the caster while a focused
 * view is active. They remain server-owned map entities, but cannot intercept
 * a projectile aimed down the canonical right-facing lane.
 */
export const ABILITY_GROUNDS_PARK_X = -600;

export interface AbilityGroundsPrepareRequest {
  /** Omit for the full baseline; provide one authored id for an isolated view. */
  targetId?: string;
}

export type AbilityGroundsPrepareCode =
  | 'MEMORY_MODE_REQUIRED'
  | 'GM_REQUIRED'
  | 'WRONG_MAP'
  | 'TARGET_INVALID'
  | 'PLAYER_NOT_FOUND'
  | 'PREPARE_FAILED';

export interface AbilityGroundsPrepareResult {
  ok: boolean;
  code?: AbilityGroundsPrepareCode;
  mapId: string;
  heroClass: string | null;
  abilityCount: number;
  spawn?: { x: number; y: number };
  targetId?: string;
  /** These are cast-policy facts, not mutations written to the character. */
  bypasses: {
    progression: boolean;
    mana: boolean;
    cooldowns: boolean;
    gcd: boolean;
    range: boolean;
  };
  persisted: false;
}

export function isAbilityGroundsMap(mapId: string | null | undefined): boolean {
  return mapId === ABILITY_GROUNDS_MAP_ID;
}
